import "rxjs/add/operator/delay";
import "rxjs/add/operator/do";
import "rxjs/add/operator/finally";
import "rxjs/add/observable/of";
import "rxjs/add/observable/fromPromise";
import "rxjs/add/operator/map";
import {ChartIQDataFeed} from "./ChartIQDataFeed";
import {ChartIQInterval, ChartIQTimeUnit} from "../types/ChartIQ";
import {IHttpService} from "angular";
import moment, {Duration, Moment, unitOfTime} from "moment";
import {StringKeyed} from "../../../types/GenericTypes";
import {Optional} from "../../../js/util/Optional";
import {TfChartDataDailySeries, TfChartDataSeries} from "../models/TfChartData";
import {IsoDateString, OffsetDateTime} from "../../../pseudo-types/DateTypes";

declare const Papa: any;

export class QuoteFeedService extends ChartIQDataFeed {

    public static id = "quoteFeedService";

    private readonly isoDateFormat = "YYYY-MM-DD";
    private readonly isoDateFormatWithTime = "YYYY-MM-DDTHH:mm:ss";

    private readonly max_periods: StringKeyed<Duration> = {
        day: moment.duration(600, "d"),
        minute: moment.duration(7, "d"),
    };

    /** @ngInject */
    public constructor(private $http: IHttpService) {
        super();
    }

    protected async retrieveData(symbol: string,
                                 startDate: Date,
                                 endDate: Date,
                                 period: null,
                                 interval: null): Promise<TfChartDataDailySeries>;

    protected async retrieveData(symbol: string,
                                 startDate: Date,
                                 endDate: Date | null,
                                 period: number | null,
                                 interval: ChartIQInterval | null): Promise<TfChartDataSeries> {
        const params = this.generateRequestParams(startDate, endDate, period, interval);
        const raw = await this.requestData(symbol, params);
        const csv = this.parseCsv(raw);
        return this.toSeries(csv, interval);
    }

    private async requestData(symbol: string, params: StringKeyed<string>): Promise<string> {
        const config = {
            params,
            ignoreLoadingBar: true
        };
        try {
            const response = await this.$http.get<string>(`/tradefloor/api/prices/chart/${symbol}`, config);
            return response.data!;
        } catch (error) {
            return Promise.resolve("");
        }
    }

    // TODO This needs to be done a lot better, a lot of this logic could do with being moved up a bit into the base
    // TODO ChartIQDataFeed so that we don't have to do all the null checks all over the place
    /* An end date of "null" means "give me all data from [startDate] until now" */
    private generateRequestParams(startDate: Date,
                                  endDate: Date | null,
                                  period: number | null,
                                  interval: ChartIQInterval | ChartIQTimeUnit | null): StringKeyed {

        const params: StringKeyed<any> = {};
        const chartInterval = Optional.of(interval)
                                      .orElse("day") as unitOfTime.Base;
        const maxPeriod = Optional.of(this.max_periods[chartInterval])
                                  .orElse(this.max_periods["day"]);
        let toDate: Moment;

        if (endDate == null) {
            if (chartInterval === "minute") {
                toDate = moment().tz("Australia/Sydney"); // up to date now.
            } else {
                toDate = moment(startDate).add(period!, chartInterval);
            }
        } else {
            toDate = moment(endDate);
        }

        let fromDate = moment(startDate);
        const earliestFromDate = toDate.clone().subtract(maxPeriod);
        if (earliestFromDate.isAfter(fromDate)) {
            fromDate = earliestFromDate;
        }

        const dateTimeFormat = interval === "minute" ? this.isoDateFormatWithTime : this.isoDateFormat;
        params.from = fromDate.format(dateTimeFormat);
        params.to = toDate.format(dateTimeFormat);
        if (interval === "minute") {
            params.i = period!;
        }
        return params;
    }

    private parseCsv(raw: string): Array<Array<string>> {
        const rows = Papa.parse(raw).data;
        rows.shift(); // remove the header row

        const rowHasData = (row: Array<string>): boolean => {
            return row != null && row.length > 0 && row[0] != null && row[0] !== "";
        };

        return rows.filter(rowHasData);
    }

    private toSeries(data: Array<Array<string>>, interval: ChartIQInterval | null): TfChartDataSeries {
        return data.map(entry => {
            const iv30 = parseFloat(entry[5]);
            const ivRank = parseFloat(entry[6]);
            const volume = parseFloat(entry[7]);
            const date = (interval == null) ? IsoDateString(entry[0]) : entry[0] as OffsetDateTime;
            return {
                Date: date,
                Open: parseFloat(entry[1]),
                High: parseFloat(entry[2]),
                Low: parseFloat(entry[3]),
                Close: parseFloat(entry[4]),
                IV30: iv30 * 100,
                IvRank: ivRank,
                Volume: volume
            };
        });
    }
}
